import { FIELD_LABELS_DA } from "@/api/cle-api/errors";

/**
 * Danish text for the per-field validation results returned by cle-compliance-api.
 *
 * The API's `reason` strings are English and intended for logs and diagnostics.
 * Translation is keyed on the exact English string because `participantName`
 * alone emits three different reasons, and the distinction is what tells a
 * caseworker whether the name was unreadable or simply did not match.
 *
 * The reason strings are pinned by
 * cle-compliance-api/tests/test_field_validation_reason_contract.py — if that
 * test fails, this map needs the same edit. Mirrored in
 * aika-selvbetjening-frontend/src/features/cle-compliance/utils/fieldValidationMessages.ts.
 */

const normalizeReason = (reason: string): string =>
  reason.trim().replace(/\s+/g, " ").toLowerCase();

const REASON_TEXT_DA_SOURCE: Record<string, string> = {
  "Document must be a legal course certificate":
    "Dokumentet er ikke vurderet som et juridisk kursusbevis.",
  "Course is not relevant to the lawyer's professional practice":
    "Kurset er vurderet til ikke at være relevant for advokatens virke.",
  "Course relevance could not be determined from the document":
    "Kursets faglige relevans kunne ikke afgøres ud fra beviset.",
  // AI'en har bevidst ikke taget stilling (ledelse, kommunikation, retorik) —
  // det er ikke en dom over kurset, så teksten må ikke lyde som en afvisning.
  "Course relevance requires assessment by a case handler":
    "Kursets relevans skal vurderes af en sagsbehandler. AI'en har ikke taget stilling.",
  "Course provider is required": "Kursusudbyderen fremgår ikke af beviset.",
  "Course date is required": "Kursusdatoen fremgår ikke af beviset.",
  "Course was partly held outside the efteruddannelse period":
    "Kurset er delvist gennemført uden for advokatens efteruddannelsesperiode. Ret antallet af lektioner til den del, der ligger i perioden, før beviset godkendes.",
  "Course end date is before the course date":
    "Kursets slutdato ligger før startdatoen, så kursusperioden kunne ikke aflæses.",
  "Course time is required":
    "Kursets samlede varighed fremgår ikke af beviset.",
  "Number of lessons is required": "Antal lektioner fremgår ikke af beviset.",
  "Course must comprise at least 3 lessons":
    "Kurset skal omfatte mindst 3 lektioner.",
  "Lesson count must be stated as lektioner or kursuslektioner":
    "Antallet skal fremgå af beviset som lektioner eller kursuslektioner.",
  // Retired by the API in favour of the signature-or-logo wording below, but
  // still stored on analysis runs recorded before, so keep it.
  "Certificate must be signed":
    "Beviset er ikke underskrevet af kursusudbyderen.",
  "Certificate must be signed or carry the course provider's logo":
    "Beviset er hverken underskrevet af kursusudbyderen eller forsynet med udbyderens logo eller stempel.",
  "Course description is required":
    "En beskrivelse af kursets indhold fremgår ikke af beviset.",
  "Certificate participant name could not be extracted":
    "Deltagerens navn kunne ikke læses på beviset.",
  "Uploader display name is not available for verification":
    "Uploaderens navn kunne ikke hentes, så deltagernavnet blev ikke kontrolleret.",
  "Certificate name does not match the uploading user":
    "Deltagernavnet på beviset stemmer ikke med brugerens navn.",
  "Insurance company is not on the approved list":
    "Forsikringsselskabet står ikke på listen over godkendte selskaber.",
  "Lawyer liability amount must be at least 2,500,000 DKK":
    "Advokatansvarsdækningen skal være mindst 2.500.000 kr.",
  "Guarantee amount must be at least 5,000,000 DKK":
    "Garantibeløbet skal være mindst 5.000.000 kr.",
};

const REASON_TEXT_DA: Record<string, string> = Object.fromEntries(
  Object.entries(REASON_TEXT_DA_SOURCE).map(([reason, text]) => [
    normalizeReason(reason),
    text,
  ]),
);

const GENERIC_REASON_TEXT = "Feltet mangler eller opfylder ikke kravene.";

/** The API tolerates both camelCase and PascalCase keys, so normalize on lookup. */
export const normalizeFieldKey = (key: string): string =>
  key.length > 0 ? `${key[0].toLowerCase()}${key.slice(1)}` : key;

export const getCleFieldLabel = (key: string): string =>
  FIELD_LABELS_DA[normalizeFieldKey(key)] ?? key;

/**
 * Resolve Danish text for a failing field: exact reason match, then a
 * label-derived fallback, then a generic message. Never returns the English
 * input — an unrecognized reason means the API reworded a string.
 */
export const resolveCleFieldValidationReason = (
  key: string,
  reason: string | null | undefined,
): string => {
  if (reason) {
    const translated = REASON_TEXT_DA[normalizeReason(reason)];

    if (translated) {
      return translated;
    }
  }

  const label = FIELD_LABELS_DA[normalizeFieldKey(key)];

  return label
    ? `${label} mangler eller opfylder ikke kravene.`
    : GENERIC_REASON_TEXT;
};
